import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Pencil, Trash2, Upload as UploadIcon, X, Eye, EyeOff, ImageOff } from 'lucide-react';
import api, { resolveImageUrl } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';

const emptyForm = {
  id: null,
  title: '',
  artist: '',
  genre: 'Pop',
  release_date: new Date().toISOString().slice(0, 10),
  cover_image: '',
  spotify_url: '',
  youtube_url: '',
  description: '',
  published: true,
};

const GENRES = ['Pop', 'Dangdut', 'Daerah', 'Jawa', 'Bali', 'Hip Hop', 'Religi', 'Rock', 'EDM', 'Jazz', 'Lainnya'];

const ReleasesManager = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/releases');
      setItems(data);
    } catch (e) {
      toast({ title: 'Gagal memuat data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setForm(emptyForm);
    setModal(true);
  };

  const openEdit = (r) => {
    setForm({
      ...r,
      release_date: (r.release_date || '').slice(0, 10),
    });
    setModal(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, cover_image: data.url }));
      toast({ title: 'Upload berhasil' });
    } catch (err) {
      toast({
        title: 'Upload gagal',
        description: err?.response?.data?.detail || 'Terjadi kesalahan',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title || !form.artist || !form.release_date) {
      toast({ title: 'Lengkapi data wajib', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;

      if (form.id) {
        await api.put(`/admin/releases/${form.id}`, payload);
        toast({ title: 'Rilisan diperbarui' });
      } else {
        await api.post('/admin/releases', payload);
        toast({ title: 'Rilisan ditambahkan' });
      }
      setModal(false);
      await load();
    } catch (err) {
      toast({
        title: 'Gagal menyimpan',
        description: err?.response?.data?.detail || 'Terjadi kesalahan',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin hapus rilisan ini?')) return;
    try {
      await api.delete(`/admin/releases/${id}`);
      toast({ title: 'Rilisan dihapus' });
      await load();
    } catch (err) {
      toast({ title: 'Gagal menghapus', variant: 'destructive' });
    }
  };

  const togglePublished = async (r) => {
    try {
      await api.put(`/admin/releases/${r.id}`, { published: !r.published });
      await load();
    } catch (e) {
      toast({ title: 'Gagal mengubah status', variant: 'destructive' });
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-white font-bold text-2xl tracking-tight">Rilisan Musik</h2>
          <p className="text-neutral-500 text-sm mt-1">Kelola highlight rilisan yang tampil di homepage</p>
        </div>
        <Button
          onClick={openCreate}
          className="rounded-full bg-gradient-to-r from-[#E9C46A] to-[#D4A94F] text-black font-semibold btn-transition gold-shimmer"
        >
          <Plus className="w-4 h-4 mr-1" /> Tambah Rilisan
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 text-[#E9C46A] animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((r) => (
            <div
              key={r.id}
              className="bg-[#0f0f0f] border border-white/[0.06] rounded-2xl p-4 flex gap-4"
            >
              <div className="w-24 h-24 rounded-xl overflow-hidden bg-neutral-900 flex-shrink-0 flex items-center justify-center">
                {r.cover_image ? (
                  <img
                    src={resolveImageUrl(r.cover_image)}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <ImageOff className="w-6 h-6 text-neutral-700" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold truncate">{r.title}</h3>
                    <p className="text-neutral-500 text-sm truncate">{r.artist}</p>
                  </div>
                  <span
                    className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                      r.published
                        ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/25'
                        : 'bg-neutral-500/15 text-neutral-400 border border-neutral-500/25'
                    }`}
                  >
                    {r.published ? 'Live' : 'Draft'}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-2 text-xs text-neutral-500">
                  <span className="px-2 py-0.5 rounded-full bg-[#D4A94F]/15 text-[#E9C46A] font-semibold">
                    {r.genre}
                  </span>
                  <span>{r.release_date}</span>
                </div>
                <div className="flex items-center gap-1 mt-3">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 px-2 text-neutral-400 hover:text-[#E9C46A]"
                    onClick={() => togglePublished(r)}
                  >
                    {r.published ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 px-2 text-neutral-400 hover:text-[#E9C46A]"
                    onClick={() => openEdit(r)}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 px-2 text-neutral-400 hover:text-red-400"
                    onClick={() => handleDelete(r.id)}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div
          className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[#0d0d0d] border border-[#D4A94F]/30 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-6 border-b border-white/[0.06]">
              <h3 className="text-white font-bold text-xl">
                {form.id ? 'Edit Rilisan' : 'Tambah Rilisan Baru'}
              </h3>
              <button
                onClick={() => setModal(false)}
                className="text-neutral-400 hover:text-white p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-400 font-semibold">
                    Judul Lagu *
                  </label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="bg-black/50 border-white/10 focus-visible:ring-[#D4A94F] text-white mt-1.5"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-400 font-semibold">
                    Artis *
                  </label>
                  <Input
                    value={form.artist}
                    onChange={(e) => setForm({ ...form, artist: e.target.value })}
                    className="bg-black/50 border-white/10 focus-visible:ring-[#D4A94F] text-white mt-1.5"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-400 font-semibold">
                    Genre
                  </label>
                  <select
                    value={form.genre}
                    onChange={(e) => setForm({ ...form, genre: e.target.value })}
                    className="w-full mt-1.5 h-10 rounded-md bg-black/50 border border-white/10 text-white px-3 focus:outline-none focus:ring-2 focus:ring-[#D4A94F]"
                  >
                    {GENRES.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-400 font-semibold">
                    Tanggal Rilis *
                  </label>
                  <Input
                    type="date"
                    value={form.release_date}
                    onChange={(e) => setForm({ ...form, release_date: e.target.value })}
                    className="bg-black/50 border-white/10 focus-visible:ring-[#D4A94F] text-white mt-1.5"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-neutral-400 font-semibold">
                  Cover Image
                </label>
                <div className="mt-1.5 flex gap-3 items-start">
                  <div className="w-24 h-24 rounded-xl overflow-hidden bg-neutral-900 flex-shrink-0 flex items-center justify-center border border-white/10">
                    {form.cover_image ? (
                      <img
                        src={resolveImageUrl(form.cover_image)}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <ImageOff className="w-6 h-6 text-neutral-700" />
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <Input
                      value={form.cover_image}
                      onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                      placeholder="URL gambar atau upload..."
                      className="bg-black/50 border-white/10 focus-visible:ring-[#D4A94F] text-white"
                    />
                    <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#D4A94F]/15 border border-[#D4A94F]/30 text-[#E9C46A] text-xs font-semibold cursor-pointer hover:bg-[#D4A94F]/25 btn-transition">
                      {uploading ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <UploadIcon className="w-3 h-3" />
                      )}
                      Upload File
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleUpload}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-400 font-semibold">
                    Spotify URL
                  </label>
                  <Input
                    value={form.spotify_url}
                    onChange={(e) => setForm({ ...form, spotify_url: e.target.value })}
                    placeholder="https://open.spotify.com/..."
                    className="bg-black/50 border-white/10 focus-visible:ring-[#D4A94F] text-white mt-1.5"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-400 font-semibold">
                    YouTube URL
                  </label>
                  <Input
                    value={form.youtube_url}
                    onChange={(e) => setForm({ ...form, youtube_url: e.target.value })}
                    placeholder="https://youtu.be/..."
                    className="bg-black/50 border-white/10 focus-visible:ring-[#D4A94F] text-white mt-1.5"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-neutral-400 font-semibold">
                  Deskripsi
                </label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="bg-black/50 border-white/10 focus-visible:ring-[#D4A94F] text-white mt-1.5 resize-none"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="published"
                  checked={form.published}
                  onChange={(e) => setForm({ ...form, published: e.target.checked })}
                  className="w-4 h-4 accent-[#D4A94F]"
                />
                <label htmlFor="published" className="text-sm text-neutral-300">
                  Publish (tampilkan di homepage)
                </label>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 p-6 border-t border-white/[0.06]">
              <Button
                variant="ghost"
                onClick={() => setModal(false)}
                className="text-neutral-400 hover:text-white"
              >
                Batal
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="rounded-full bg-gradient-to-r from-[#E9C46A] to-[#D4A94F] text-black font-semibold btn-transition"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Simpan'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReleasesManager;
